import Vue from 'vue';
import { captureException, init, setContext } from '@sentry/vue';
import Store from '@/store';

import { getLaunchConfig } from '@/services/GetLaunchConfig';
import Blizzard from '@/services/Blizzard';
import App from './App.vue';
import { VueOptions } from '@sentry/vue/dist/sdk';
import { Integration } from '@sentry/types';

interface SentryTracingModule {
  Integrations: {
    BrowserTracing: new () => Integration;
  };
}

const TRACING_SAMPLE_THRESHOLD = 0.01;
global.shouldTrace = Math.random() < TRACING_SAMPLE_THRESHOLD;

let sentryTracingModulePromise: Promise<SentryTracingModule> | null = null;
const asyncModules: Promise<any>[] = [];

if (global.shouldTrace) {
  sentryTracingModulePromise = import(/* webpackChunkName: "sentry-tracing" */ '@sentry/tracing');
  asyncModules.push(sentryTracingModulePromise);
}

if (
  !('IntersectionObserver' in window) ||
  !('IntersectionObserverEntry' in window) ||
  !('intersectionRatio' in window.IntersectionObserverEntry.prototype) ||
  !('isIntersecting' in window.IntersectionObserverEntry.prototype)
) {
  // load IntersectionObserver polyfill
  asyncModules.push(
    import(/* webpackChunkName: "intersection-observer" */ 'intersection-observer'),
  );
}

if (!('fetch' in window)) {
  asyncModules.push(import(/* webpackChunkName: "fetch" */ 'whatwg-fetch'));
}

if (!('TextDecoder' in window)) {
  asyncModules.push(import(/* webpackChunkName: "fast-text-encoding" */ 'fast-text-encoding'));
}

Vue.config.productionTip = false;
console.log('Release:', BUILD_FLAGS.RELEASE_STRING);
console.log('Environment:', BUILD_FLAGS.ENVIRONMENT);

// https://docs.sentry.io/platforms/javascript/configuration/
const sentryConfig: Partial<VueOptions> = {
  Vue: Vue,
  dsn: 'https://78b2503dd22c404c93a0629c6dfd4d2b@sentry.sc-prod.net/43',
  release: BUILD_FLAGS.RELEASE_STRING,
  environment: BUILD_FLAGS.ENVIRONMENT,
  debug: BUILD_FLAGS.ENVIRONMENT !== 'production',
  logErrors: BUILD_FLAGS.ENVIRONMENT !== 'production',
};

const initialize = (sentryTracingModule?: SentryTracingModule | undefined) => {
  if (
    sentryTracingModule &&
    sentryTracingModule.Integrations &&
    sentryTracingModule.Integrations.BrowserTracing
  ) {
    sentryConfig.integrations = [new sentryTracingModule.Integrations.BrowserTracing()];
    sentryConfig.tracesSampleRate = 1;
  }
  init(sentryConfig);

  const launchConfig = getLaunchConfig(location);
  window.onload = () => {
    Blizzard.initBlizzard(launchConfig.flowMode);
    Blizzard.recordPageLoad();
  };
  function recordFirstUserInteraction() {
    Blizzard.recordFirstUserInteraction();
    window.removeEventListener('touchstart', recordFirstUserInteraction, false);
  }
  window.addEventListener('touchstart', recordFirstUserInteraction, false);

  const store = Store(
    launchConfig.initialTraits,
    launchConfig.flowMode,
    launchConfig.categoryIndex,
    launchConfig.initialSectionId,
    launchConfig.centerActiveCategoryIcon,
    launchConfig.assetOverrides,
    launchConfig.is3dMode,
    launchConfig.forceShowExitDialog,
    launchConfig.exitDialogType,
  );

  // Attach structured context to each Sentry issue
  setContext('config-and-state', {
    launch_config: JSON.stringify(launchConfig),
    store_state: JSON.stringify(store.state),
  });

  new Vue({
    // @ts-ignore
    render: (h) => h(App),
    store: store,
  }).$mount('#app');
};

if (!asyncModules.length) {
  initialize();
} else {
  Promise.all(asyncModules)
    .then(() => {
      if (!sentryTracingModulePromise) {
        initialize();
      } else {
        return sentryTracingModulePromise.then((sentryTracingModule: SentryTracingModule) => {
          initialize(sentryTracingModule);
        });
      }
    })
    .catch((e) => {
      init(sentryConfig);
      captureException(e);
    });
}
